import { Injectable } from '@angular/core';
import {
  EventSourceMessage,
  EventStreamContentType,
  fetchEventSource,
} from '@microsoft/fetch-event-source';
import { Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MAX_ERRORS, RETRY_BASE_SEC } from '../constants';
import {
  CLIENT_SERVER_ERROR_RESPONSE_RANGE,
  TOO_MANY_REQUESTS,
} from '../constants/httpResponseStatues.constant';
import { FatalError, RetriableError, SseEvent } from '../models';

@Injectable({
  providedIn: 'root',
})
export class SseService {
  public isConnected: boolean = false;
  public sseData$: Subject<SseEvent> = new Subject<SseEvent>();

  private readonly sseServerUrl: string = environment.sseServer;
  private abortController: AbortController = new AbortController();
  private errors_count: number = 0;
  public connectToSSE(jwt: string): void {
    if (this.isConnected) {
      return;
    }
    this.connect(jwt);
  }

  private connect(jwt: string) {
    fetchEventSource(this.sseServerUrl, {
      method: 'GET',
      headers: {
        jwt: jwt,
        'content-type': 'application/json',
      },
      signal: this.abortController.signal,
      onopen: async (response: Response) => {
        if (
          response.ok &&
          response.headers.get('content-type') === EventStreamContentType
        ) {
          console.log('Connection to SSE has been successfully established...');
          this.isConnected = true;
          return; // everything's good
        } else if (
          response.status >= CLIENT_SERVER_ERROR_RESPONSE_RANGE[0] &&
          response.status <= CLIENT_SERVER_ERROR_RESPONSE_RANGE[1] &&
          response.status !== TOO_MANY_REQUESTS
        ) {
          // client-side errors are usually non-retriable:
          console.error(response);
          //alert(response);
          throw new FatalError();
        } else {
          console.log('Retirable error ', response);
        }
      },
      onclose: () => {
        console.debug('Closed from server');
        throw new RetriableError();
      },
      onmessage: (evMsg: EventSourceMessage) => {
        switch (evMsg.event) {
          case 'CONNECTED':
            this.isConnected = true;
            break;
          case 'CLOSE':
            this.abortSSE();
            console.debug('this._doSubscribe() ==>  event');
            break;
          default:
            const data = JSON.parse(evMsg.data);
            console.debug('handle data ==>');
            console.debug(data);
            this.sseData$.next(data);
        }
      },
      onerror: (err) => {
        console.debug('SSE disconnect', err);
        if (err instanceof FatalError) {
          throw err; // rethrow to stop the operation
        } else if (this.errors_count < MAX_ERRORS) {
          this.errors_count++;
        } else {
          this.abortSSE();
          this.errors_count = 0;
          throw err;
        }
        console.debug('Reconnecting...' + RETRY_BASE_SEC ** this.errors_count);
        return RETRY_BASE_SEC ** this.errors_count * 1000;
      },
    });
  }
  public abortSSE(): void {
    if (this.isConnected) {
      this.abortController.abort('Closed');
      this.abortController = new AbortController();
      console.debug('Closing event source...');
    }
    this.isConnected = false;
    this.errors_count = 0;
  }
}
